import { useMemo, useState } from 'react';
import { ApiError, api, type ParamDef } from '../api';
import { Banner, Field, JsonEditor, Pill, Spinner, useAsync } from '../components/Common';
import { ParamForm, defaultParamValues } from '../components/ParamForm';
import { DataTable } from '../components/Blocks';

interface RequestDef {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  connection_id: number;
  connection_name: string;
  connection_kind: string;
  target: string;
  http_method: string;
  params: ParamDef[];
  template: Record<string, any>;
  rows_path: string | null;
  transform: any[];
  cache_ttl_s: number;
  is_write: boolean;
  accuracy_note: string | null;
  version: number;
}

interface ConnectionLite {
  id: number;
  name: string;
  kind: string;
  tools: { name: string; description?: string; inputSchema?: any }[];
}

/**
 * Requests.
 *
 * "Edit requests" from the brief. A request binds one entry point + one tool to
 * an explicit argument template, a row path, and a transform pipeline. The tester
 * on the right is the point of the screen: it shows the resolved arguments, the
 * raw upstream response, and the rows after transforms — which is how you find
 * out that a count came back as a string, or that `rows_path` is wrong, before a
 * manager sees a zero and believes it.
 */
export function Requests() {
  const requests = useAsync(() => api.get<RequestDef[]>('/api/requests'));
  const connections = useAsync(() => api.get<ConnectionLite[]>('/api/connections'));
  const [selected, setSelected] = useState<RequestDef | 'new' | null>(null);

  if (requests.loading && !requests.data) return <Spinner label="Loading requests…" />;
  if (requests.error) return <Banner tone="error">{requests.error}</Banner>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Requests</h1>
          <p>
            Each request is one call to one entry point, with explicit arguments. Test every request before a report
            depends on it.
          </p>
        </div>
        <div className="page-actions">
          <button className="primary" onClick={() => setSelected('new')}>
            New request
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Request</th>
                <th>Entry point</th>
                <th>Calls</th>
                <th className="right">Cache</th>
                <th>Flags</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(requests.data ?? []).map((req) => (
                <tr key={req.id}>
                  <td>
                    <strong>{req.name}</strong>
                    <div className="mono" style={{ color: 'var(--muted)' }}>
                      {req.slug}
                    </div>
                  </td>
                  <td>{req.connection_name}</td>
                  <td className="mono">{req.target}</td>
                  <td className="right">{req.cache_ttl_s ? `${Math.round(req.cache_ttl_s / 60)} min` : 'none'}</td>
                  <td>
                    {req.is_write && <Pill tone="bad">write</Pill>} {req.accuracy_note && <Pill tone="warn">caveat</Pill>}
                  </td>
                  <td className="right">
                    <button className="small" onClick={() => setSelected(req)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!(requests.data ?? []).length && <p className="note">No requests yet.</p>}
      </div>

      {selected && (
        <RequestEditor
          request={selected === 'new' ? null : selected}
          connections={connections.data ?? []}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            requests.reload();
          }}
        />
      )}
    </>
  );
}

function RequestEditor({
  request,
  connections,
  onClose,
  onSaved,
}: {
  request: RequestDef | null;
  connections: ConnectionLite[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: request?.name ?? '',
    description: request?.description ?? '',
    connection_id: request?.connection_id ?? connections[0]?.id ?? 0,
    target: request?.target ?? '',
    http_method: request?.http_method ?? 'POST',
    rows_path: request?.rows_path ?? 'data',
    cache_ttl_s: request?.cache_ttl_s ?? 900,
    is_write: request?.is_write ?? false,
    accuracy_note: request?.accuracy_note ?? '',
  });
  const [params, setParams] = useState<ParamDef[]>(request?.params ?? []);
  const [template, setTemplate] = useState<Record<string, any>>(request?.template ?? {});
  const [transform, setTransform] = useState<any[]>(request?.transform ?? []);
  const [valid, setValid] = useState({ params: true, template: true, transform: true });
  const [error, setError] = useState<string | null>(null);
  const [errorList, setErrorList] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const connection = connections.find((c) => c.id === form.connection_id);
  const tools = connection?.tools ?? [];
  const selectedTool = tools.find((t) => t.name === form.target);

  const save = async () => {
    if (!valid.params || !valid.template || !valid.transform) {
      setError('Fix the invalid JSON before saving.');
      return;
    }
    setBusy(true);
    setError(null);
    setErrorList([]);
    try {
      const payload = { ...form, params, template, transform };
      if (request) await api.put(`/api/requests/${request.id}`, payload);
      else await api.post('/api/requests', payload);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setErrorList(Array.isArray(err.detail) ? (err.detail as string[]) : []);
      } else setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!request) return;
    setBusy(true);
    try {
      await api.del(`/api/requests/${request.id}`);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ borderColor: 'var(--brand)' }}>
      <h3>{request ? `Edit “${request.name}”` : 'New request'}</h3>
      {error && <Banner tone="error" items={errorList.length > 1 ? errorList : undefined}>{errorList.length > 1 ? null : error}</Banner>}

      <div className="split">
        <div>
          <div className="row">
            <Field label="Name">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Entry point">
              <select value={form.connection_id} onChange={(e) => setForm({ ...form, connection_id: Number(e.target.value) })}>
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {connection?.kind === 'process' ? (
            <Field
              label="Script or command"
              help="A .py or .ps1 path is passed to the interpreter as a script; anything else is passed through as a verb your wrapper script dispatches on. Relative paths resolve against the entry point's cwd."
            >
              <input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="bh_export.py" />
            </Field>
          ) : connection?.kind === 'http' ? (
            <div className="row">
              <Field label="Path" help="Appended to the entry point's base URL, e.g. dialpad/callStats. {placeholders} in the path are filled from arguments.">
                <input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} />
              </Field>
              <Field label="Method">
                <select value={form.http_method} onChange={(e) => setForm({ ...form, http_method: e.target.value })}>
                  {['POST', 'GET', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
              </Field>
            </div>
          ) : (
            <Field
              label="Tool"
              help={
                tools.length
                  ? 'Chosen from the tools discovered on the entry point.'
                  : 'No tools discovered yet — refresh the tool list on the entry point first, or type the name if you know it.'
              }
            >
              {tools.length ? (
                <select value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })}>
                  <option value="">— choose a tool —</option>
                  {tools.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} />
              )}
            </Field>
          )}

          {selectedTool && (
            <div className="banner info" style={{ marginBottom: 14 }}>
              <span className="icon" aria-hidden>
                i
              </span>
              <div>
                <strong>{selectedTool.name}</strong>
                {selectedTool.description && <div style={{ fontSize: 12.5 }}>{selectedTool.description}</div>}
                {selectedTool.inputSchema?.properties && (
                  <div className="mono" style={{ fontSize: 11.5, marginTop: 6 }}>
                    {Object.entries(selectedTool.inputSchema.properties)
                      .map(([key, schema]: [string, any]) => `${key}: ${schema?.type ?? 'any'}`)
                      .join('  ·  ')}
                  </div>
                )}
              </div>
            </div>
          )}

          <JsonEditor
            label="Parameters this request accepts"
            help='Array of definitions, e.g. [{"name":"dateRange","label":"Period","type":"daterange","required":true,"default":{"preset":"this_month"}}]'
            value={params}
            rows={7}
            onChange={(parsed, ok) => {
              setValid((v) => ({ ...v, params: ok }));
              if (ok) setParams(Array.isArray(parsed) ? parsed : []);
            }}
          />

          <JsonEditor
            label="Arguments sent upstream"
            help="Placeholders: {{param.name}}, {{param.dateRange.from}}, {{ctx.today}}, {{ctx.userEmail}}. A value that is exactly one placeholder keeps its type, so {{param.maxRows}} stays a number."
            value={template}
            rows={9}
            onChange={(parsed, ok) => {
              setValid((v) => ({ ...v, template: ok }));
              if (ok) setTemplate(parsed ?? {});
            }}
          />

          <div className="row">
            <Field label="Rows path" help='Where the array lives in the response, e.g. "data". Leave blank only if the response is already an array.'>
              <input value={form.rows_path ?? ''} onChange={(e) => setForm({ ...form, rows_path: e.target.value })} />
            </Field>
            <Field label="Cache for (seconds)" help="0 = always live. Keep high for Dialpad: 200 requests/hour limit.">
              <input type="number" min={0} value={form.cache_ttl_s} onChange={(e) => setForm({ ...form, cache_ttl_s: Number(e.target.value) })} />
            </Field>
          </div>

          <JsonEditor
            label="Transform pipeline"
            help="Ordered steps: select, drop, rename, coerce, filter, sort, limit, fill, dedupe, derive, group, join, validate. No expressions — each step is a declared operation."
            value={transform}
            rows={10}
            onChange={(parsed, ok) => {
              setValid((v) => ({ ...v, transform: ok }));
              if (ok) setTransform(Array.isArray(parsed) ? parsed : []);
            }}
          />

          <Field
            label="Accuracy caveat"
            help="Shown on every report that uses this request, on screen, in the PDF and on the Excel cover sheet. Use it for anything approximate."
          >
            <textarea rows={2} value={form.accuracy_note} onChange={(e) => setForm({ ...form, accuracy_note: e.target.value })} />
          </Field>

          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginBottom: 14 }}>
            <input type="checkbox" checked={form.is_write} onChange={(e) => setForm({ ...form, is_write: e.target.checked })} />
            <span>
              This request <strong>changes data</strong> upstream. Writes are never retried and always require explicit
              confirmation.
            </span>
          </label>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="primary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button onClick={onClose} disabled={busy}>
              Cancel
            </button>
            {request && (
              <button className="danger" onClick={remove} disabled={busy} style={{ marginLeft: 'auto' }}>
                Delete
              </button>
            )}
          </div>
        </div>

        <div>{request ? <RequestTester request={request} params={params} /> : <Banner tone="info">Save the request, then test it here.</Banner>}</div>
      </div>
    </div>
  );
}

interface TestResult {
  ok: boolean;
  resolvedArgs: Record<string, any>;
  durationMs: number;
  rawSample: string | null;
  extractedColumns: string[];
  extractedCount: number;
  rows: Record<string, any>[];
  rowCount: number;
  columns: string[];
  warnings: string[];
}

function RequestTester({ request, params }: { request: RequestDef; params: ParamDef[] }) {
  const [values, setValues] = useState<Record<string, any>>(() => defaultParamValues(params));
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolvedOnError, setResolvedOnError] = useState<Record<string, any> | null>(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'rows' | 'args' | 'raw'>('rows');

  const columns = useMemo(() => (result?.columns ?? []).map((key) => ({ key, label: key, format: 'text' as const })), [result]);

  const runTest = async () => {
    setBusy(true);
    setError(null);
    setResolvedOnError(null);
    try {
      const payload: Record<string, any> = { params: values };
      if (request.is_write) {
        // eslint-disable-next-line no-alert
        if (!window.confirm('This request is marked as a write. Testing it will change live data in the upstream system. Continue?')) {
          setBusy(false);
          return;
        }
        payload.confirmWrite = true;
      }
      const next = await api.post<TestResult>(`/api/requests/${request.id}/test`, payload);
      setResult(next);
      setTab('rows');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      const detail = err instanceof ApiError ? (err.detail as any) : null;
      if (detail?.resolvedArgs) setResolvedOnError(detail.resolvedArgs);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ background: 'var(--page)' }}>
      <h3>Test this request</h3>
      {params.length > 0 && <ParamForm defs={params} values={values} onChange={setValues} disabled={busy} />}
      <button className="primary" onClick={runTest} disabled={busy} style={{ marginBottom: 12 }}>
        {busy ? 'Calling…' : 'Run test call'}
      </button>

      {error && (
        <Banner tone="error" title="The call failed. ">
          {error}
          {resolvedOnError && (
            <>
              <div style={{ marginTop: 8, fontSize: 12 }}>Arguments that were sent:</div>
              <pre className="raw">{JSON.stringify(resolvedOnError, null, 2)}</pre>
            </>
          )}
        </Banner>
      )}

      {result && (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>
            <span>
              <strong>{result.rowCount}</strong> rows after transforms
            </span>
            <span>
              {result.extractedCount} extracted from the response
            </span>
            <span>{result.durationMs} ms</span>
          </div>

          {result.warnings.length > 0 && <Banner tone="warn" title="Worth checking" items={result.warnings} />}

          {result.extractedCount === 0 && (
            <Banner tone="error" title="Nothing was extracted. ">
              Either the upstream returned no records, or the rows path does not match the response shape. Check the Raw tab.
            </Banner>
          )}

          <div className="tabs">
            <button className={tab === 'rows' ? 'active' : ''} onClick={() => setTab('rows')}>
              Rows
            </button>
            <button className={tab === 'args' ? 'active' : ''} onClick={() => setTab('args')}>
              Arguments sent
            </button>
            <button className={tab === 'raw' ? 'active' : ''} onClick={() => setTab('raw')}>
              Raw response
            </button>
          </div>

          {tab === 'rows' && <DataTable rows={result.rows} columns={columns} pageSize={15} />}
          {tab === 'args' && <pre className="raw">{JSON.stringify(result.resolvedArgs, null, 2)}</pre>}
          {tab === 'raw' && <pre className="raw">{result.rawSample ?? 'No raw sample captured.'}</pre>}
        </>
      )}
    </div>
  );
}
